import {
  CustomIndexText,
  DemoCoinIcon,
  Divider,
  IndexWrapper,
  InputContainer,
  InputPriceWrapper,
  InputWrapper,
  LoaderWrapper,
  StrippedInput,
} from '../styled'
import {
  PresentationalStateActions,
  ReceivedToken,
  TransactionState,
} from '../../transactional-context/interface'
import React, { useEffect } from 'react'
import { useDebounce, useERC20New } from 'hooks'

import { BalanceLoader } from 'components/balance-loader'
import { BigNumber } from 'ethers'
import { DEFAULT_INDEX_DECIMALS } from 'app-constants'
import { InputProps } from '..'
import { SingleSelectInfo } from '../types'
import { accumulatedDollarValueFromTokensReceived } from '../../transactional-context/useOutDollarValue'
import { assetsListBySymbol } from 'abis/set/asset-icons'
import { currencyFormat } from '../../../../../utils/formatting-utils'
import { enforcer } from '../services'
import { marketCapNumber } from 'utils'
import { useRefresh } from 'services'
import { useStore } from 'services/store'
import { useTourGuide } from 'context/tour-guide/use-tour-guide'
import { useTransactionalContext } from '../../transactional-context'

export const defaultSelectInfo = {
  isMint: false,
  target: '0x0',
  outputAmount: BigNumber.from(0),
}

export const BuyInputField: React.FC<InputProps> = ({ indexInfo, finishedLoading }) => {
  const [selectInfo, setSelectInfo] = React.useState<SingleSelectInfo>(defaultSelectInfo)
  const [inputDollarValue, setInputDollarValue] = React.useState<string>('0')
  const {
    dispatch,
    isBuying,
    selectedAsset,
    inputAssetAmount,
    loadingValues,
    transactionState,
    productAddress,
  } = useTransactionalContext()
  const [value, setValue] = React.useState<string>(inputAssetAmount)
  const [loading, setLoading] = React.useState<boolean>(loadingValues)
  const { network } = useStore()
  const { restartSlowTimer } = useRefresh()
  const { decimals: sellDecimals } = useERC20New(selectedAsset)
  const debouncedValue = useDebounce<string>(value, 300)
  const { setCurrentStep, currentStep, run, startTour } = useTourGuide()

  const nativeTokenPrice = '1'
  const usdcPrice = '64'

  const handleInput = (target: HTMLInputElement) => {
    enforcer(target, setValue)
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined
    if (finishedLoading && currentStep === 10) {
      setValue('1')
      setInputDollarValue('1744.56')
      timeout = setTimeout(() => {
        //find element by class id
        const element = document.getElementsByClassName('__floater__body')[0]
        if (run && !element) {
          startTour()
          setCurrentStep(currentStep + 1)
          setValue('1')
          setInputDollarValue('1744.56')
        }
      }, 750)
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [currentStep, run, finishedLoading])

  const dispatchLoading = (flag: boolean) => {
    setLoading(flag)
    dispatch({
      type: PresentationalStateActions.SET_LOADING_FOR_VALUES,
      payload: flag,
    })
  }

  const updateReceivedSection = () => {
    const payload = [
      value !== ''
        ? {
            token: productAddress,
            nativePrice: nativeTokenPrice,
            usdcPrice: usdcPrice,
            decimals: DEFAULT_INDEX_DECIMALS,
            ...selectInfo,
          }
        : {
            token: productAddress,
            nativePrice: nativeTokenPrice,
            usdcPrice: usdcPrice,
            decimals: DEFAULT_INDEX_DECIMALS,
            ...defaultSelectInfo,
          },
    ]
    const totalOutputValue = accumulatedDollarValueFromTokensReceived(
      payload as ReceivedToken[],
      indexInfo.basePrice,
      network || 1,
      isBuying,
    )

    dispatch({
      type: PresentationalStateActions.SET_RECEIVED_TOKENS,
      payload: {
        receivedTokens: payload,
        outputDollarValue: totalOutputValue.toString(),
      },
    })
  }

  const handleOutputs = async () => {
    if (debouncedValue !== '1') {
      return
    }
    dispatchLoading(true)
    if (Number(debouncedValue) > 0) {
      dispatchLoading(true)

      setTimeout(() => {
        setInputDollarValue('1744.56')
        dispatchLoading(false)
        if (currentStep === 9) {
          setCurrentStep(currentStep + 1)
        }
      }, 2500)
    } else {
      setSelectInfo(defaultSelectInfo)
    }
  }

  const ethereumObject = assetsListBySymbol['ETH']

  /**
   * IT: sets the input dollar value
   * WHEN: The input dollar value changes
   * WHY: Because the value is required in other parts of the widget
   */
  useEffect(() => {
    updateReceivedSection()
    dispatch({
      type: PresentationalStateActions.SET_INPUT_DOLLAR_VALUE,
      payload: inputDollarValue,
    })
  }, [inputDollarValue])

  useEffect(() => {
    setLoading(true)
    restartSlowTimer()
    dispatch({
      type: PresentationalStateActions.CHANGE_BUY_INPUT_AMOUNT,
      payload: debouncedValue,
    })
    handleOutputs()
  }, [debouncedValue])

  useEffect(() => {
    setValue(inputAssetAmount)
  }, [inputAssetAmount])

  return (
    <InputContainer open={false} id="tour-input-box">
      <InputWrapper>
        <StrippedInput
          type="text"
          value={value}
          onChange={({ target }) => handleInput(target)}
          autoComplete="off"
          autoCorrect="off"
          inputMode="decimal"
          pattern={`^[0-9]*[.,]?[0-9]{0,${sellDecimals || 18}}$`}
          placeholder="0.00"
          minLength={1}
          maxLength={100}
          spellCheck="false"
          disabled={transactionState === TransactionState.SUBMITTING}
        />
        {
          <InputPriceWrapper>
            {debouncedValue && Number(debouncedValue) > 0 && !loading ? (
              `~${
                inputDollarValue.length > 11
                  ? `$${marketCapNumber(inputDollarValue)}`
                  : currencyFormat(inputDollarValue)
              }`
            ) : loading && debouncedValue !== '' ? (
              <LoaderWrapper>
                {`~$`}
                <BalanceLoader gray={true} />
              </LoaderWrapper>
            ) : (
              `~$0.00`
            )}
          </InputPriceWrapper>
        }
      </InputWrapper>
      <Divider />
      <IndexWrapper>
        <DemoCoinIcon src={ethereumObject.icon} />
        <CustomIndexText type={'p'}>{ethereumObject.symbol}</CustomIndexText>
      </IndexWrapper>
    </InputContainer>
  )
}
